import React, { useState } from "react";
import { ScrollView, StyleSheet, View, Text, ActivityIndicator } from "react-native";
import Queries from "../../../api/queries";
import { mobUser } from "../../../mobx/MobUser";
import { MD3Theme, useTheme } from "react-native-paper";
import { useFocusEffect } from "@react-navigation/native";
import i18n from "../../../i18n";
import { SafeAreaView } from "react-native-safe-area-context";
import FooterDivider from "../../../components/FooterDivider";

export default function Welcome({ route }) {
  const { field = "" } = route.params;
  const theme = useTheme();
  const styles = styleGen(theme);
  const [welcomes, setWelcomes] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const queries = new Queries();

  const fetchWelcomeData = async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await queries.getWelcomeGuideField(
        mobUser.OiqiaBookingId,
        mobUser.Language || 'en',
        field
      );

      if (result?.data?.getWelcomeGuideField?.text) {
        setWelcomes(result.data.getWelcomeGuideField.text);
      }
    } catch (err) {
      console.error("Erreur lors du chargement du guide de bienvenue:", err);
      setError("Erreur lors du chargement du contenu. Veuillez réessayer.");
      setWelcomes("");
    } finally {
      setLoading(false);
    }
  };

  useFocusEffect(
    React.useCallback(() => {
      if (field && mobUser.OiqiaBookingId) {
        fetchWelcomeData();
      } else {
        setLoading(false);
        setError("Paramètres manquants pour charger le contenu.");
      }
    }, [field, mobUser.OiqiaBookingId, mobUser.Language])
  );

  const renderTitle = () => {

    let titleOfPage = "";

    const POSSIBLE_TITLES = {
      "discoverCity": i18n.t("home.welcomeNotes.exploreTheCity"),
      "localFavorites": i18n.t("home.welcomeNotes.localFavorites"),
      "essentialsAround": i18n.t("home.welcomeNotes.essentials"),
      "gettingAround": i18n.t("home.welcomeNotes.gettingAround"),
      "usefulNumbers": i18n.t("home.welcomeNotes.usefullNumbers"),
    }

    if (POSSIBLE_TITLES[field]) {
      titleOfPage = POSSIBLE_TITLES[field];
    }

    return titleOfPage;
  }

  const renderContent = () => {
    if (loading) {
      return (
        <View style={styles.centerContainer}>
          <ActivityIndicator size="large" color={theme.colors.primary} />
          <Text style={styles.loadingText}>Chargement...</Text>
        </View>
      );
    }

    if (error) {
      return (
        <View style={styles.centerContainer}>
          <Text style={styles.errorText}>{error}</Text>
        </View>
      );
    }

    if (!welcomes) {
      return (
        <View style={styles.centerContainer}>
          <Text style={styles.emptyText}>{i18n.t("errors.emptyContent")}</Text>
        </View>
      );
    }

    return (
      <Text style={styles.contentText}>{welcomes}</Text>
    );
  };

  return (
    <SafeAreaView style={{ flex: 1 }} edges={['bottom']}>
      <ScrollView contentContainerStyle={styles.container}>
        <View style={styles.subContainer}>
          <Text style={styles.title}>{renderTitle()}</Text>
          {renderContent()}
        </View>
        <FooterDivider />
      </ScrollView>
    </SafeAreaView>
  );
}

const styleGen = (theme: MD3Theme) => StyleSheet.create({
  container: {
    backgroundColor: theme.colors.background,
    alignItems: "center",
    paddingVertical: 20,
  },
  subContainer: {
    borderColor: theme.colors.secondary,
    borderWidth: 3,
    padding: 20,
    paddingBottom: 20,
    borderRadius: 20,
    alignItems: "center",
    gap: 20,
    marginTop: 10,
    width: '90%',
  },
  centerContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 100,
  },
  contentText: {
    fontSize: 16,
    lineHeight: 24,
    color: theme.colors.onBackground,
    textAlign: 'left',
    width: '100%',
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
    color: theme.colors.onBackground,
  },
  errorText: {
    fontSize: 16,
    color: theme.colors.error,
    textAlign: 'center',
  },
  emptyText: {
    fontSize: 16,
    color: theme.colors.onSurfaceVariant,
    textAlign: 'center',
    fontStyle: 'italic',
  },
  title: {
    fontSize: 25,
    textAlign: "center",
    fontFamily: "NotoSans-Bold",
  },
});